import { useEffect, useMemo, useRef } from 'react';
import type { Map, GeoJSONSource } from 'mapbox-gl';
import { useMapBox } from './BalloonEventsMapBox';
import { useMapHotspots, EXCLUDED_PROVIDERS } from '../hooks/useMapHotspots';
import { useBalloonEventsMetadata, useQuery } from 'react-ui';
import type { HotspotFeatureCollection } from 'ts-core';

const SOURCE_ID = 'all-balloons-source';
const LAYER_ID = 'all-balloons-layer';

const EMPTY_COLLECTION: HotspotFeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

function addBalloonsLayer(map: Map, initialData: HotspotFeatureCollection): void {
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: initialData,
    });
  }

  if (!map.getLayer(LAYER_ID)) {
    map.addLayer({
      id: LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': 3,
        'circle-opacity': 0.25,
      },
    });
  }
}

function removeBalloonsLayer(map: Map): void {
  if (map.getLayer(LAYER_ID)) {
    map.removeLayer(LAYER_ID);
  }
  if (map.getSource(SOURCE_ID)) {
    map.removeSource(SOURCE_ID);
  }
}

/**
 * Renders a small colored dot at each hotspot cell position, visible at every
 * zoom level. Dot color reflects the event category; size is fixed at 5 px.
 * Use VITE_HOTSPOT_DISPLAY=dots to activate this layer instead of HotspotsLayer.
 */
export function AllBalloonsLayer() {
  const map = useMapBox()?.map ?? null;
  const { metadata } = useBalloonEventsMetadata();
  const { query } = useQuery();
  // Partner-scoped map mode narrows the dots to the partner's data-source
  // provider via query.providers; otherwise show every (non-excluded) provider.
  const providers = useMemo(
    () =>
      query.providers && query.providers.length > 0
        ? query.providers
        : metadata.providers.map((p) => p.shortName).filter((s) => !EXCLUDED_PROVIDERS.includes(s)),
    [metadata, query.providers]
  );
  // Mirror HotspotsLayer: narrow by tag only for saved-search-bound tags
  // (forceMapClustering is the saved-search opt-in).
  const tags = useMemo(
    () => (query.forceMapClustering && query.tags ? query.tags : []),
    [query.forceMapClustering, query.tags]
  );
  // User-scoped map mode: limit dots to one user's events.
  const user = useMemo(() => query.user ?? [], [query.user]);
  const { data } = useMapHotspots(map, providers, tags, user);

  const dataRef = useRef<HotspotFeatureCollection | null>(null);
  dataRef.current = data;

  // Add (and re-add after setStyle resets) once the map is truly idle
  useEffect(() => {
    if (!map) return;

    const setup = () => {
      if (!map.isStyleLoaded() || map.getSource(SOURCE_ID)) return;
      addBalloonsLayer(map, dataRef.current ?? EMPTY_COLLECTION);
    };

    map.on('idle', setup);
    setup();

    return () => {
      map.off('idle', setup);
      if (map.isStyleLoaded()) {
        removeBalloonsLayer(map);
      }
    };
  }, [map]);

  // Push updated data into the source whenever it changes
  useEffect(() => {
    if (!map) return;
    const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    if (source) {
      source.setData(data ?? EMPTY_COLLECTION);
    }
  }, [map, data]);

  return null;
}
